// --------------------------------------------------------------
// Created On: 2021-05-14
// Author: Zachary Thomas
//
// Last Modified: 2026-10-30
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect, Fragment } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HamburgerButton from "./HamburgerButton/HamburgerButton";
import NavbarLink from "../NavbarLink/NavbarLink";
import NavbarDropdown from "../NavbarDropdown/NavbarDropdown";
import UserOptions from "./UserOptions/UserOptions";
import CompanyOptions from "./CompanyOptions/CompanyOptions";
import NotificationIndicator from "./NotificationIndicator/NotificationIndicator";
import AiAgentButton from "../AiAgentButton/AiAgentButton";
import SearchBar from "../SearchBar/SearchBar";
import { Link } from "react-router-dom";
import useReduxSelector from "../../hooks/useReduxSelector";
import {
  RPM_COMPANY,
  PUBLIC_PAGES,
  APP_BRAND,
  RPM_BRAND,
  APP_IS_IN_DEV_ENV,
  CICONNECT_BRAND,
  CICONNECT_BETA_BRAND,
} from "../../constants/miscellaneous";
import userHasPermission from "../../utilities/userHasPermission";
import userHasCrossCompanyAccess from "../../utilities/userHasCrossCompanyAccess";
import userHasCrossSubsidiaryAccess from "src/utilities/userHasCrossSubsidiaryAccess";
import userHasUserPermissions from "../../utilities/userHasUserPermissions";
import userHasRolePermissions from "../../utilities/userHasRolePermissions";
import {
  ALIAS_ATTRIBUTES_PERMISSION,
  CREATE_ASSETS_PERMISSION,
  CREATE_ASSET_GROUPS_PERMISSION,
  CREATE_ASSET_RENTALS_PERMISSION,
  CREATE_REPORTS_PERMISSION,
  DELETE_ASSETS_PERMISSION,
  DELETE_ASSET_GROUPS_PERMISSION,
  REVOKE_ASSET_RENTALS_PERMISSION,
  RETURN_ASSET_RENTALS_PERMISSION,
  DELETE_REPORTS_PERMISSION,
  UPDATE_ASSETS_PERMISSION,
  UPDATE_ASSET_GROUPS_PERMISSION,
  UPDATE_ASSET_RENTALS_PERMISSION,
  UPDATE_REPORTS_PERMISSION,
  VIEW_SUBSCRIPTIONS_PERMISSION,
  VIEW_DEVICE_STATUSES_PERMISSION,
  MANAGE_ALERT_THRESHOLDS_PERMISSION,
  ACCEPT_ASSET_RENTALS_PERMISSION,
  DECLINE_ASSET_RENTALS_PERMISSION,
  TRANSFER_ASSETS_PERMISSION,
  ACCEPT_ASSET_TRANSFERS_PERMISSION,
  DECLINE_ASSET_TRANSFERS_PERMISSION,
  CREATE_TEMPLATES_PERMISSION,
  UPDATE_TEMPLATES_PERMISSION,
  DELETE_TEMPLATES_PERMISSION,
  VIEW_GLOBAL_RPM_REPORTS_PERMISSION,
  CREATE_COMPANY_PERMISSION,
  UPDATE_COMPANY_PERMISSION,
  DELETE_COMPANY_PERMISSION,
  CROSS_COMPANY_PERMISSION,
  MODIFY_ACTIVATION_STATUS_PERMISSION,
  CROSS_SUBSIDIARY_PERMISSION,
  CREATE_SUBSIDIARY_PERMISSION,
  RELEASE_SUBSIDIARY_PERMISSION,
  AGENT_ACCESS_PERMISSION,
} from "../../constants/permissions";
import encodeCompleteUri from "../../utilities/encodeCompleteUri";
import styles from "./Navbar.module.scss";

// General navbar.
export default function Navbar(props: Props): Component {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [inCompany, setInCompany] = useState<boolean>(true);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const logoBrandingStyle = getLogoBrandingStyle(APP_BRAND);
  const location = useLocation();
  const navigate = useNavigate();

  // Keep track of the status of the current user for routing purposes.
  useEffect(() => {
    setInCompany(currentUser.companyName !== RPM_COMPANY);
  }, [JSON.stringify(currentUser)]);

  // Clear the search query and navigate to the results page.
  function goToResults(resultsAddress: string): void {
    setSearchQuery("");
    navigate(resultsAddress);
  }

  // Gets the list of management pages based on the user's current permissions.
  function getManagementPages(): Page[] {
    const managementPages: Page[] = [];

    if (
      userHasPermission([
        [CREATE_ASSETS_PERMISSION],
        [UPDATE_ASSETS_PERMISSION],
        [DELETE_ASSETS_PERMISSION],
        [CREATE_ASSET_GROUPS_PERMISSION],
        [UPDATE_ASSET_GROUPS_PERMISSION],
        [DELETE_ASSET_GROUPS_PERMISSION],
      ])
    ) {
      managementPages.push({ name: "Assets", targetPage: "/manage/assets" });
    }

    if (
      userHasPermission([[CREATE_TEMPLATES_PERMISSION], [UPDATE_TEMPLATES_PERMISSION], [DELETE_TEMPLATES_PERMISSION]])
    ) {
      managementPages.push({ name: "Templates", targetPage: "/manage/templates" });
    }

    if (userHasUserPermissions()) {
      managementPages.push({ name: "Users", targetPage: "/manage/users" });
    }

    if (userHasRolePermissions()) {
      managementPages.push({ name: "Roles", targetPage: "/manage/roles" });
    }

    if (userHasPermission([[CREATE_REPORTS_PERMISSION], [UPDATE_REPORTS_PERMISSION], [DELETE_REPORTS_PERMISSION]])) {
      managementPages.push({ name: "Reports", targetPage: "/manage/reports" });
    }

    if (
      userHasPermission([
        [CREATE_ASSET_RENTALS_PERMISSION],
        [UPDATE_ASSET_RENTALS_PERMISSION],
        [REVOKE_ASSET_RENTALS_PERMISSION],
        [RETURN_ASSET_RENTALS_PERMISSION],
        [ACCEPT_ASSET_RENTALS_PERMISSION],
        [DECLINE_ASSET_RENTALS_PERMISSION],
      ])
    ) {
      managementPages.push({ name: "Rentals", targetPage: "/manage/rentals" });
    }

    if (
      userHasPermission([
        [TRANSFER_ASSETS_PERMISSION],
        [ACCEPT_ASSET_TRANSFERS_PERMISSION],
        [DECLINE_ASSET_TRANSFERS_PERMISSION],
      ])
    ) {
      managementPages.push({ name: "Transfers", targetPage: "/manage/transfers" });
    }

    managementPages.push({ name: "Views", targetPage: "/manage/views" });

    if (
      userHasPermission([
        [CROSS_SUBSIDIARY_PERMISSION],
        [CREATE_SUBSIDIARY_PERMISSION],
        [RELEASE_SUBSIDIARY_PERMISSION],
      ])
    ) {
      managementPages.push({ name: "Subsidiaries", targetPage: "/manage/subsidiaries" });
    }

    return managementPages;
  }

  // Gets the logo styling details based on the current brand.
  function getLogoBrandingStyle(logoBrand: string): string {
    if (logoBrand === CICONNECT_BRAND || logoBrand === CICONNECT_BETA_BRAND) {
      return styles.controlsIncLogo;
    } else {
      return styles.logo;
    }
  }

  // Gets the list of monitoring device pages based on the user's current permissions.
  function getMonitoringDevicePages(): Page[] {
    const monitoringDevicePages: Page[] = [];

    if (userHasPermission([[VIEW_SUBSCRIPTIONS_PERMISSION]])) {
      monitoringDevicePages.push({
        name: "Subscriptions",
        targetPage: "/monitoring-devices/subscriptions",
      });
    }

    if (userHasPermission([[VIEW_DEVICE_STATUSES_PERMISSION]])) {
      monitoringDevicePages.push({
        name: "Statuses",
        targetPage: "/monitoring-devices/statuses",
      });
    }

    return monitoringDevicePages;
  }

  // Get the list of data processing pages based on the user's current permissions.
  function getDataProcessingPages(): Page[] {
    const dataProcessingPages: Page[] = [];

    if (userHasPermission([[ALIAS_ATTRIBUTES_PERMISSION]])) {
      dataProcessingPages.push({
        name: "Attribute Aliases",
        targetPage: "/data-processing/attributes",
      });
    }

    dataProcessingPages.push({
      name: "Attribute Information",
      targetPage: "/data-processing/attribute-info",
    });

    if (userHasPermission([[MANAGE_ALERT_THRESHOLDS_PERMISSION]])) {
      dataProcessingPages.push({
        name: "Alert Thresholds",
        targetPage: "/data-processing/alert-thresholds",
      });
    }

    return dataProcessingPages;
  }

  // Return the list of super admin pages based on the user's current permissions.
  function getSuperAdminPages(): Page[] {
    const superAdminPages: Page[] = [{ name: "Companies", targetPage: "/dashboard/companies" }];
    if (userHasPermission([[VIEW_GLOBAL_RPM_REPORTS_PERMISSION]])) {
      superAdminPages.push({ name: "Chatty Assets", targetPage: "/assets/chatty" });
    }
    if (userHasPermission([[CREATE_COMPANY_PERMISSION], [UPDATE_COMPANY_PERMISSION], [DELETE_COMPANY_PERMISSION]])) {
      superAdminPages.push({ name: "Manage Companies", targetPage: "/manage/companies" });
    }
    if (userHasUserPermissions()) {
      superAdminPages.push({ name: "Manage Users", targetPage: "/manage/users" });
    }
    if (userHasRolePermissions()) {
      superAdminPages.push({ name: "Manage Roles", targetPage: "/manage/roles" });
    }
    if (userHasPermission([[VIEW_GLOBAL_RPM_REPORTS_PERMISSION]])) {
      superAdminPages.push({ name: "Reports", targetPage: "/reports" });
    }
    superAdminPages.push({ name: "Devices", targetPage: "/device-search" });
    if (userHasPermission([[CROSS_COMPANY_PERMISSION, UPDATE_ASSETS_PERMISSION]])) {
      superAdminPages.push({ name: "Asset Configuration", targetPage: "/asset-configuration" });
    }
    if (userHasPermission([[CROSS_COMPANY_PERMISSION, MODIFY_ACTIVATION_STATUS_PERMISSION]])) {
      superAdminPages.push({ name: "Activation Status", targetPage: "/activation-status" });
    }
    return superAdminPages;
  }

  // Select a logo based on the passed in app logo environment variable.
  function getLogoUrl(logoBrand: string, isSmallDisplay: boolean): string {
    let fileType = "png";
    let fileName = "logo";
    if (logoBrand === RPM_BRAND) {
      fileType = "svg";
    }
    if (isSmallDisplay) {
      fileName = "logo_small";
    }
    const logoUrl = `/${fileName}.${fileType}`;
    return logoUrl;
  }

  return (
    <nav className={`${styles.body} navbar navbar-expand-xl px-1 px-sm-2 px-lg-3`}>
      <div className={styles.navInner}>
        {/* Left group: hamburger, logo, and navigation links. */}
        <div className={styles.leftGroup}>
          <div className={styles.start}>
            {/* Hamburger menu button. */}
            <HamburgerButton onClick={() => props.onOpenNavigationDrawer()} />

            {/* Application branded logo. */}
            <Link className={`${styles.brand} p-0 m-0`} to={PUBLIC_PAGES.includes(props.currentPage) ? "/login" : "/"}>
              {/* Full sized logo. */}
              <img
                className={`${logoBrandingStyle} m-0 d-none d-lg-inline-block`}
                src={getLogoUrl(APP_BRAND, false)}
                alt="Application Logo"
              />

              {/* Small version of logo - for smaller displays. */}
              <img
                className={`${logoBrandingStyle} m-0 d-inline-block d-lg-none`}
                src={getLogoUrl(APP_BRAND, true)}
                alt="Application Logo"
              />

              {APP_IS_IN_DEV_ENV && (
                <img className={styles.logoOverlay} src="/images/dev_overlay.svg" alt="Dev Logo Overlay" />
              )}
            </Link>
          </div>

          {/* Navigation links. */}
          <div className={styles.linkList}>
            <ul className="navbar-nav d-none d-xl-flex">
              {PUBLIC_PAGES.includes(props.currentPage) ? (
                <Fragment>
                  <NavbarLink name="Login" targetPage="/login" currentPage={props.currentPage} />
                  <NavbarLink name="Create Account" targetPage="/register" currentPage={props.currentPage} />
                  <NavbarLink name="Reset Password" targetPage="/forgot-password" currentPage={props.currentPage} />
                  {props.currentPage === "/accept-invite" && (
                    <NavbarLink name="Company Invite" targetPage="/accept-invite" currentPage={props.currentPage} />
                  )}
                  {props.currentPage === "/privacy-policy-viewer" && (
                    <NavbarLink
                      name="Privacy Policy"
                      targetPage="/privacy-policy-viewer"
                      currentPage={props.currentPage}
                    />
                  )}
                  {props.currentPage === "/privacy-policy-text" && (
                    <NavbarLink
                      name="Privacy Policy"
                      targetPage="/privacy-policy-text"
                      currentPage={props.currentPage}
                    />
                  )}
                  {props.currentPage === "/eula-viewer" && (
                    <NavbarLink name="EULA" targetPage="/eula-viewer" currentPage={props.currentPage} />
                  )}
                  {props.currentPage === "/eula-text" && (
                    <NavbarLink name="EULA" targetPage="/eula-text" currentPage={props.currentPage} />
                  )}
                </Fragment>
              ) : (
                <Fragment>
                  {inCompany ? (
                    <Fragment>
                      {!currentUser.isPackager && (
                        <NavbarDropdown
                          name="Data Dashboards"
                          targetPage="/dashboard"
                          currentPage={props.currentPage}
                          menuItems={
                            props.companyHasSystemControlAccess
                              ? [
                                  { name: "Asset Map", targetPage: "/dashboard/map" },
                                  { name: "Asset List", targetPage: "/dashboard/list" },
                                  { name: "Asset Graph", targetPage: "/dashboard/graph" },
                                  { name: "System Control", targetPage: "/dashboard/system-control" },
                                ]
                              : [
                                  { name: "Asset Map", targetPage: "/dashboard/map" },
                                  { name: "Asset List", targetPage: "/dashboard/list" },
                                  { name: "Asset Graph", targetPage: "/dashboard/graph" },
                                ]
                          }
                        />
                      )}

                      <NavbarDropdown
                        name="Manage Resources"
                        targetPage="/manage"
                        currentPage={props.currentPage}
                        menuItems={getManagementPages()}
                      />

                      {getDataProcessingPages().length > 0 && (
                        <NavbarDropdown
                          name="Data Processing"
                          targetPage="/data-processing"
                          currentPage={props.currentPage}
                          menuItems={getDataProcessingPages()}
                        />
                      )}

                      {getMonitoringDevicePages().length > 0 && (
                        <NavbarDropdown
                          name="Monitoring Devices"
                          targetPage="/monitoring-devices"
                          currentPage={props.currentPage}
                          menuItems={getMonitoringDevicePages()}
                        />
                      )}

                      {props.currentPage === "/asset" && (
                        <NavbarLink name="Asset Details" currentPage={location.pathname} />
                      )}

                      {props.currentPage === "/scan-asset" && (
                        <NavbarLink name="Process QR Code" currentPage={location.pathname} />
                      )}

                      {props.currentPage === "/review-account" && (
                        <NavbarLink name="Review Request" currentPage={location.pathname} />
                      )}

                      {props.currentPage === "/asset-review" && (
                        <NavbarLink name="Review Request" currentPage={location.pathname} />
                      )}
                    </Fragment>
                  ) : (
                    <NavbarDropdown
                      name="RPM Dashboard"
                      targetPage="/dashboard"
                      currentPage={props.currentPage}
                      menuItems={getSuperAdminPages()}
                    />
                  )}

                  {props.currentPage === "/search" && <NavbarLink name="Search" currentPage={location.pathname} />}

                  {props.currentPage === "/notification" && (
                    <NavbarLink
                      name="Notifications"
                      targetPage="/notification/overview"
                      currentPage={props.currentPage}
                    />
                  )}

                  {props.currentPage === "/account" && (
                    <NavbarLink name="Account Settings" targetPage="/account" currentPage={props.currentPage} />
                  )}
                </Fragment>
              )}
            </ul>
          </div>
        </div>

        {/* Right group: search bar and action buttons (non-public pages only). */}
        {!PUBLIC_PAGES.includes(props.currentPage) && (
          <div className={styles.rightGroup}>
            {/* Search bar. */}
            {currentUser.companyName !== RPM_COMPANY && !currentUser.isPackager && (
              <SearchBar
                prompt="Search..."
                location="NAVBAR"
                searchQuery={searchQuery}
                onChange={(searchQuery) => setSearchQuery(searchQuery)}
                onSubmit={() => goToResults(`/search/${encodeCompleteUri(searchQuery)}`)}
              />
            )}

            <div className={styles.extraOptions}>
              {/* AI Agent Button. */}
              {inCompany && userHasPermission([[AGENT_ACCESS_PERMISSION]]) && props.companyHasAgentAccess && (
                <AiAgentButton location="NAVBAR" onClick={() => props.onOpenAgentChatWindow()} />
              )}

              {/* Alert indicator that polls periodically. */}
              <NotificationIndicator />

              {/* Company information (for RPM admins). */}
              {((userHasCrossCompanyAccess() && currentUser.companyName !== RPM_COMPANY) ||
                (userHasCrossSubsidiaryAccess() &&
                  currentUser.userCompanyPath !== undefined &&
                  currentUser.userCompanyPath.length > 0)) && <CompanyOptions />}

              {/* Menu of user options. */}
              <UserOptions />
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

interface Page {
  name: string;
  targetPage: string;
}

interface Props {
  currentPage: string;
  companyHasAgentAccess: boolean;
  companyHasSystemControlAccess: boolean;
  onOpenNavigationDrawer: () => void;
  onOpenAgentChatWindow: () => void;
}
